import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useEffect, useState } from "react";
import api from "../api/axios";
import Logo from "./Logo";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const onDashboard = pathname === "/dashboard";

  const [contactedCount, setContactedCount] = useState(0);

  const fetchCount = () => {
    if (user?.role !== "student") return;
    api.get("/contacts/my-contacted-count")
      .then(r => setContactedCount(r.data.count || 0))
      .catch(() => {});
  };

  useEffect(() => { fetchCount(); }, [user]);

  useEffect(() => {
    const handler = () => fetchCount();
    window.addEventListener("pp:contacts-updated", handler);
    return () => window.removeEventListener("pp:contacts-updated", handler);
  }, [user]);

  const handleLogout = () => { logout(); nav("/"); };

  return (
    <nav style={{
      background: "rgba(6,6,18,0.92)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      borderBottom: "1px solid rgba(249,115,22,0.10)",
      position: "sticky", top: 0, zIndex: 100,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .nav-wrap {
          display: flex; align-items: center; justify-content: space-between;
          height: 60px; padding: 0 20px;
          max-width: 1160px; margin: 0 auto;
          width: 100%; box-sizing: border-box; overflow: hidden;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .nav-links {
          display: flex; gap: 6px; align-items: center;
          min-width: 0; flex-shrink: 1; overflow: hidden;
        }
        .nav-hide-mobile { display: inline-flex; }

        /* Ghost button */
        .nav-ghost {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 13px; font-weight: 500;
          color: #9ca3af; padding: 6px 14px;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 8px; background: transparent;
          cursor: pointer; text-decoration: none;
          white-space: nowrap; flex-shrink: 0;
          transition: color .18s, border-color .18s, background .18s;
          letter-spacing: 0.01em;
        }
        .nav-ghost:hover {
          color: #f97316 !important;
          border-color: rgba(249,115,22,0.35) !important;
          background: rgba(249,115,22,0.06) !important;
        }
        .nav-ghost-active {
          color: #f97316 !important;
          border-color: rgba(249,115,22,0.35) !important;
          font-weight: 700;
        }

        /* CTA pill (Register) */
        .nav-cta {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 13px; font-weight: 700;
          color: #fff; padding: 7px 20px;
          background: linear-gradient(135deg, #f97316, #ea580c);
          border: none; border-radius: 22px;
          cursor: pointer; text-decoration: none;
          white-space: nowrap; flex-shrink: 0;
          letter-spacing: 0.02em;
          box-shadow: 0 0 18px rgba(249,115,22,0.38), 0 2px 8px rgba(0,0,0,0.3);
          transition: box-shadow .18s, transform .15s;
        }
        .nav-cta:hover {
          box-shadow: 0 0 28px rgba(249,115,22,0.60), 0 4px 12px rgba(0,0,0,0.4);
          transform: translateY(-1px);
        }

        /* Name badge */
        .nav-badge {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 12px; font-weight: 600;
          color: #f97316; padding: 4px 12px;
          background: rgba(249,115,22,0.10);
          border: 1px solid rgba(249,115,22,0.22);
          border-radius: 22px; white-space: nowrap;
          letter-spacing: 0.01em;
        }

        /* Teachers count pill */
        .nav-count {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 12px; font-weight: 500;
          color: #cbd5e1; padding: 4px 12px 4px 6px;
          background: rgba(30,41,59,0.70);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 22px; white-space: nowrap;
          text-decoration: none; flex-shrink: 0;
          display: flex; align-items: center; gap: 6px;
          transition: color .18s, border-color .18s;
        }
        .nav-count:hover {
          color: #f97316;
          border-color: rgba(249,115,22,0.35);
        }
        .nav-count-dot {
          background: linear-gradient(135deg, #f97316, #ea580c);
          color: #fff; border-radius: 50%;
          width: 20px; height: 20px;
          display: flex; align-items: center; justify-content: center;
          font-size: 10px; font-weight: 800; flex-shrink: 0;
          box-shadow: 0 0 8px rgba(249,115,22,0.5);
        }

        /* Find Tutors link */
        .nav-find {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 13px; font-weight: 500;
          color: #9ca3af; padding: 6px 14px;
          border-radius: 8px; text-decoration: none;
          white-space: nowrap; flex-shrink: 0;
          transition: color .18s;
          letter-spacing: 0.01em;
        }
        .nav-find:hover { color: #f1f5f9; }

        /* Logout button */
        .nav-logout {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 13px; font-weight: 500;
          color: #6b7280; padding: 6px 14px;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 8px; background: transparent;
          cursor: pointer; white-space: nowrap; flex-shrink: 0;
          transition: color .18s, border-color .18s;
          letter-spacing: 0.01em;
        }
        .nav-logout:hover {
          color: #ef4444;
          border-color: rgba(239,68,68,0.30);
        }

        /* Profile pill */
        .nav-profile {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 12px; font-weight: 600;
          padding: 4px 14px; border-radius: 22px;
          text-decoration: none; white-space: nowrap;
          transition: all .18s; letter-spacing: 0.01em;
        }

        @media (max-width: 480px) {
          .nav-wrap { height: 54px; padding: 0 14px; }
          .nav-hide-mobile { display: none !important; }
          .nav-links { gap: 5px; }
          .nav-ghost { font-size: 11.5px; padding: 5px 10px; }
          .nav-cta   { font-size: 12px; padding: 6px 14px; }
          .nav-count { font-size: 11px; padding: 3px 10px 3px 5px; }
          .nav-badge { font-size: 11px; padding: 3px 10px; }
          .nav-logout { font-size: 11.5px; padding: 5px 10px; }
        }
      `}</style>

      <div className="nav-wrap">

        {/* Logo */}
        <Link to="/" style={{ textDecoration: "none", flexShrink: 0 }}>
          <Logo size={30} />
        </Link>

        {/* Navigation links */}
        <div className="nav-links">

          {/* Guest */}
          {!user && (
            <>
              <Link to="/search" className="nav-find nav-hide-mobile">
                Find Teachers
              </Link>
              <Link to="/login" className="nav-ghost">
                Login
              </Link>
              <Link to="/register" className="nav-cta">
                Register
              </Link>
            </>
          )}

          {/* Student */}
          {user?.role === "student" && (
            <>
              <span className="nav-badge nav-hide-mobile">
                👋 {user.name?.split(" ")[0]}
              </span>
              {contactedCount > 0 && (
                <Link to="/my-teachers" className="nav-count">
                  <span className="nav-count-dot">{contactedCount}</span>
                  Teachers
                </Link>
              )}
              <button onClick={handleLogout} className="nav-logout">
                Logout
              </button>
            </>
          )}

          {/* Teacher */}
          {user?.role === "teacher" && (
            <>
              <Link
                to="/dashboard"
                className={`nav-ghost ${onDashboard ? "nav-ghost-active" : ""}`}
              >
                Dashboard
              </Link>
              {user.teacher_id ? (
                <Link
                  to={`/teacher/${user.teacher_id}`}
                  className="nav-profile nav-hide-mobile"
                  style={{
                    color: onDashboard ? "#9ca3af" : "#f97316",
                    background: onDashboard ? "rgba(30,41,59,0.5)" : "rgba(249,115,22,0.10)",
                    border: `1px solid ${onDashboard ? "rgba(255,255,255,0.08)" : "rgba(249,115,22,0.25)"}`,
                  }}
                >
                  🧑‍🏫 {user.name?.split(" ")[0]}
                </Link>
              ) : (
                <Link to="/dashboard" className="nav-profile nav-hide-mobile"
                  style={{ color: "#9ca3af", background: "transparent", border: "1px solid rgba(255,255,255,0.08)" }}>
                  🧑‍🏫 {user.name?.split(" ")[0]}
                </Link>
              )}
              <button onClick={handleLogout} className="nav-logout">
                Logout
              </button>
            </>
          )}

          {/* Admin */}
          {user?.role === "admin" && (
            <>
              <Link to="/admin" className="nav-ghost nav-ghost-active">
                Admin Panel
              </Link>
              <button onClick={handleLogout} className="nav-logout">
                Logout
              </button>
            </>
          )}

        </div>
      </div>
    </nav>
  );
}
